import { z } from 'zod';
import { EnrollmentStatusSchema, TrialEnrollmentDtoSchema } from './enrollments.schema.js';
// ---------------------------------------------------------------------------
// Explicit physician decisions about one participation (issue #29,
// REQ-PAT-014/032/033; frozen feature
// spec/features/patients-pathways/treatment-stages.feature; Q-PAT-008 closed
// at the 2026-10-21 gate; approved wireframe
// spec/domains/patients-pathways/ui/treatment-decisions.ui.md)
// ---------------------------------------------------------------------------
/**
 * The full decision vocabulary of `enrollment_decisions` (0021 CHECK
 * `enrollment_decisions_type_valid`).
 *
 * DELIBERATE duplication of `ENROLLMENT_DECISION_TYPES` in `@ctms/db` (same
 * decision of 2026-09-27 as `ENROLLMENT_STATUSES`): `@ctms/contracts` stays
 * free of a dependency on the schema package, and the database keeps its own
 * CHECK constraint on the same nine values. If one list changes, both must
 * change.
 *
 * The qualification pair belongs to the #30 dialog flow
 * (`DecideQualificationRequestSchema` in `criteria.schema.ts`) and only ever
 * appears in the history — the #29 panel request vocabulary is
 * `PANEL_DECISION_TYPES` below.
 */
export const ENROLLMENT_DECISION_TYPES = {
    QUALIFICATION_POSITIVE: 'qualification_positive',
    QUALIFICATION_NEGATIVE: 'qualification_negative',
    ENROLL_IN_TREATMENT: 'enroll_in_treatment',
    STAGE_IMPROVEMENT: 'stage_improvement',
    STAGE_NO_IMPROVEMENT: 'stage_no_improvement',
    END_TREATMENT: 'end_treatment',
    WITHDRAW_CONSENT: 'withdraw_consent',
    END_FOLLOW_UP: 'end_follow_up',
    LOST_CONTACT: 'lost_contact',
};
export const EnrollmentDecisionTypeSchema = z.enum(Object.values(ENROLLMENT_DECISION_TYPES));
/** Stage decisions (Q-PAT-008): history entry only, NO status transition. */
export const STAGE_DECISION_TYPES = ['stage_improvement', 'stage_no_improvement'];
/**
 * The seven decisions the #29 participation panel may record (approved
 * wireframe „Dostępność decyzji wg statusu udziału"). Which of them is
 * available from which status is the backend matrix (Q-PAT-008) — the UI only
 * offers what the panel read returned.
 */
export const PANEL_DECISION_TYPES = [
    'enroll_in_treatment',
    'stage_improvement',
    'stage_no_improvement',
    'end_treatment',
    'withdraw_consent',
    'end_follow_up',
    'lost_contact',
];
export const PanelDecisionTypeSchema = z.enum(PANEL_DECISION_TYPES);
/**
 * One append-only history row (`enrollment_decisions` of 0021; REQ-PAT-033:
 * kto — `userId`, kiedy — `createdAt`, co — `decisionType` + the status pair).
 * Never edited, never deleted (wireframe: „Historia jest niezmienna").
 *
 * The refine mirrors the 0021 CHECK `enrollment_decisions_stage_pair_valid`:
 * stage decisions carry the NULL/NULL pair, transition decisions carry BOTH
 * statuses.
 */
export const EnrollmentDecisionDtoSchema = z
    .object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    enrollmentId: z.number().int().positive(),
    decisionType: EnrollmentDecisionTypeSchema,
    fromStatus: EnrollmentStatusSchema.nullable(),
    toStatus: EnrollmentStatusSchema.nullable(),
    /** Optional physician comment saved with the decision. */
    comment: z.string().nullable(),
    /** Actor — the site-staff account that recorded the decision. */
    userId: z.number().int().positive(),
    /**
     * Actor display name resolved through the users cluster; null when the
     * account no longer resolves on this site (kit-destruction precedent —
     * a dangling reference must not block the decision history).
     */
    userDisplayName: z.string().nullable(),
    createdAt: z.iso.datetime(),
})
    .refine((row) => STAGE_DECISION_TYPES.includes(row.decisionType)
    ? row.fromStatus === null && row.toStatus === null
    : row.fromStatus !== null && row.toStatus !== null, { message: 'Para from/to musi być spójna z rodzajem decyzji (stage: null/null)' });
/**
 * The participation panel read (`GET /patients/:patientId/enrollments/
 * :enrollmentId/decisions`): the current status, the decisions recordable from
 * it (Q-PAT-008 matrix — empty in terminal statuses, where the panel is
 * read-only) and the newest-first history. `items` rather than a bare array
 * (precedent: `TrialCriteriaConfigDto`); no pagination — one enrollment's
 * decision log is short and rendered whole.
 */
export const DecisionPanelDtoSchema = z.object({
    status: EnrollmentStatusSchema,
    availableDecisions: z.array(PanelDecisionTypeSchema),
    items: z.array(EnrollmentDecisionDtoSchema),
});
/**
 * `POST …/decisions`: one explicit physician decision. The availability
 * matrix and the per-decision target status are backend rules; `comment` is
 * optional and blank normalizes to `null` in the service. Recording NEVER
 * touches the visit schedule (rescope 2026-10-21 — the schedule stays purely
 * manual).
 */
export const RecordDecisionRequestSchema = z.object({
    decisionType: PanelDecisionTypeSchema,
    comment: z.string().trim().nullish(),
});
/** The participation after the atomic write plus its new history row. */
export const RecordDecisionResponseSchema = z.object({
    enrollment: TrialEnrollmentDtoSchema,
    decision: EnrollmentDecisionDtoSchema,
});
//# sourceMappingURL=decisions.schema.js.map